<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  endpoint: { type: Object, required: true },
});
defineEmits(['close']);
const { t } = useI18n();

// Nomes de campo aceitos pelo endpoint. Sao tokens tecnicos, iguais em
// qualquer idioma: ficam aqui para nao virar texto solto no template.
const FIELD_ALIASES = {
  name: 'nome|name',
  phone: 'telefone|phone|whatsapp|celular',
  email: 'email|e-mail',
};
const ALL_FIELD_ALIASES = {
  name: 'nome | name | firstname | first_name | fullname | full_name',
  phone:
    'telefone | phone | whatsapp | celular | tel | mobile | cellphone | fone',
  email: 'email | e-mail | mail | correio',
};

const baseHost = computed(() => {
  try { return new URL(props.endpoint.public_url).origin; } catch (e) { return 'https://staging.siteup.com.br'; }
});

const buttonHtml = computed(() => `<a href="${props.endpoint.public_url}" target="_blank" rel="noopener">
  Falar no WhatsApp
</a>`);

// Form HTML puro: o usuario poe a URL no action e o browser faz o resto.
// Apos POST: redirecionamos pro WhatsApp do lead com mensagem pronta.
const simpleFormHtml = computed(() => `<!-- Cole esse form no seu site (sem JavaScript) -->
<form action="${props.endpoint.public_url}" method="POST">
  <input name="nome"     type="text"  placeholder="Seu nome" required />
  <input name="telefone" type="tel"   placeholder="Seu WhatsApp (com DDD)" required />
  <input name="email"    type="email" placeholder="Seu e-mail" />

  <!-- Honeypot anti-spam (deixe oculto, nao mexa) -->
  <input name="website" style="display:none" tabindex="-1" autocomplete="off" />

  <button type="submit">Quero falar no WhatsApp</button>
</form>`);

// Versao avancada: captura UTMs/gclid/fbclid da URL atual da pagina via JS.
// Necessaria so se o lead chega na sua landing JA com ?gclid=... na URL e
// voce quer levar isso pro tracking. Pra anuncios Google Ads que apontam
// direto pro form, nao precisa.
const advancedFormHtml = computed(() => `<!-- Avancado: captura gclid/UTMs da URL antes de enviar -->
<form id="meu-form" action="${props.endpoint.public_url}" method="POST">
  <input name="nome"     type="text"  placeholder="Seu nome" required />
  <input name="telefone" type="tel"   placeholder="Seu WhatsApp" required />
  <input name="email"    type="email" placeholder="Seu e-mail" />

  <!-- Hidden fields preenchidos via JS abaixo -->
  <input type="hidden" name="gclid" />
  <input type="hidden" name="fbclid" />
  <input type="hidden" name="utm_source" />
  <input type="hidden" name="utm_medium" />
  <input type="hidden" name="utm_campaign" />

  <button type="submit">Enviar</button>
</form>

<script>
(function(){
  var p = new URLSearchParams(window.location.search);
  ['gclid','fbclid','utm_source','utm_medium','utm_campaign'].forEach(function(k){
    var el = document.querySelector('#meu-form input[name="' + k + '"]');
    if (el && p.get(k)) el.value = p.get(k);
  });
})();
</` + 'script>');

const curlExample = computed(() => `curl -X POST '${props.endpoint.public_url}' \
  -H 'Content-Type: application/json' \
  -d '{
    "name": "Joao da Silva",
    "phone": "+5551989342801",
    "email": "joao@exemplo.com",
    "gclid": "Cj0KCQ...",
    "utm_source": "google",
    "utm_campaign": "dedetizacao_2026"
  }'`);

function copyText(text) {
  navigator.clipboard
    .writeText(text)
    .then(() => useAlert(t('INTEGRATION_SETTINGS.LEAD_CAPTURE.ALERTS.COPIED')))
    .catch(() => useAlert(t('INTEGRATION_SETTINGS.LEAD_CAPTURE.ALERTS.COPY_ERROR')));
}
</script>

<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-n-alpha-black2 backdrop-blur-[4px]" @click.self="$emit('close')">
    <div class="bg-n-background rounded-xl w-[90%] max-w-3xl max-h-[90vh] flex flex-col shadow-xl">
      <header class="flex justify-between items-center px-6 py-4 border-b border-n-slate-4">
        <h3 class="text-base font-semibold text-n-slate-12">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.SNIPPET.TITLE', { name: endpoint.name }) }}</h3>
        <button type="button" class="inline-flex items-center justify-center size-11 text-n-slate-11 hover:text-n-slate-12" :aria-label="$t('INTEGRATION_SETTINGS.LEAD_CAPTURE.SNIPPET.CLOSE')" @click="$emit('close')"><span class="i-ri-close-line size-5" /></button>
      </header>

      <div class="px-6 py-5 overflow-y-auto flex-1">
        <section class="mb-6">
          <h4 class="text-sm font-semibold text-n-slate-12 mb-1">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.SNIPPET.PUBLIC_URL') }}</h4>
          <p class="text-xs text-n-slate-11 mb-2">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.SNIPPET.PUBLIC_URL_HINT') }}</p>
          <div class="bg-n-slate-2 border border-n-slate-4 rounded p-3 relative">
            <code class="block text-xs font-mono break-all text-n-slate-12">{{ endpoint.public_url }}</code>
            <button class="absolute top-2 right-2 text-xs px-2 py-1 rounded border border-n-slate-4 bg-n-background hover:bg-n-slate-3" @click="copyText(endpoint.public_url)">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.SNIPPET.COPY') }}</button>
          </div>
        </section>

        <section v-if="endpoint.endpoint_type === 'wa_redirect'" class="mb-6">
          <h4 class="text-sm font-semibold text-n-slate-12 mb-1">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.SNIPPET.BUTTON') }}</h4>
          <p class="text-xs text-n-slate-11 mb-2">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.SNIPPET.BUTTON_HINT') }}</p>
          <div class="bg-n-slate-2 border border-n-slate-4 rounded p-3 relative">
            <pre class="text-xs font-mono whitespace-pre-wrap text-n-slate-12">{{ buttonHtml }}</pre>
            <button class="absolute top-2 right-2 text-xs px-2 py-1 rounded border border-n-slate-4 bg-n-background hover:bg-n-slate-3" @click="copyText(buttonHtml)">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.SNIPPET.COPY') }}</button>
          </div>
        </section>

        <section v-if="endpoint.endpoint_type === 'webhook'" class="mb-6">
          <h4 class="text-sm font-semibold text-n-slate-12 mb-1">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.SNIPPET.SIMPLE_FORM') }}</h4>
          <p class="text-xs text-n-slate-11 mb-2">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.SNIPPET.SIMPLE_FORM_HINT') }}</p>
          <div class="bg-n-slate-2 border border-n-slate-4 rounded p-3 relative">
            <pre class="text-xs font-mono whitespace-pre-wrap text-n-slate-12">{{ simpleFormHtml }}</pre>
            <button class="absolute top-2 right-2 text-xs px-2 py-1 rounded border border-n-slate-4 bg-n-background hover:bg-n-slate-3" @click="copyText(simpleFormHtml)">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.SNIPPET.COPY') }}</button>
          </div>
          <p class="text-xs text-n-slate-11 mt-2">
            {{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.SNIPPET.FIELD_ALIASES') }} <code>{{ FIELD_ALIASES.name }}</code>, <code>{{ FIELD_ALIASES.phone }}</code>, <code>{{ FIELD_ALIASES.email }}</code>.
          </p>
        </section>

        <section v-if="endpoint.endpoint_type === 'webhook'" class="mb-6">
          <details>
            <summary class="text-sm font-semibold text-n-slate-12 cursor-pointer">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.SNIPPET.PLATFORMS') }}</summary>
            <div class="mt-3 space-y-3 text-xs text-n-slate-11">
              <div class="bg-n-slate-2 border border-n-slate-4 rounded p-3">
                <p class="font-semibold text-n-slate-12 mb-1">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.SNIPPET.WORDPRESS_TITLE') }}</p>
                <p>{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.SNIPPET.WORDPRESS_BODY') }}</p>
              </div>
              <div class="bg-n-slate-2 border border-n-slate-4 rounded p-3">
                <p class="font-semibold text-n-slate-12 mb-1">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.SNIPPET.WP_PLUGINS_TITLE') }}</p>
                <p>{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.SNIPPET.WP_PLUGINS_BODY') }}</p>
              </div>
              <div class="bg-n-slate-2 border border-n-slate-4 rounded p-3">
                <p class="font-semibold text-n-slate-12 mb-1">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.SNIPPET.BUILDERS_TITLE') }}</p>
                <p>{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.SNIPPET.BUILDERS_BODY') }}</p>
              </div>
              <div class="bg-n-slate-2 border border-n-slate-4 rounded p-3">
                <p class="font-semibold text-n-slate-12 mb-1">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.SNIPPET.CODED_TITLE') }}</p>
                <p>{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.SNIPPET.CODED_BODY') }}</p>
              </div>
              <div class="bg-n-slate-2 border border-n-slate-4 rounded p-3">
                <p class="font-semibold text-n-slate-12 mb-1">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.SNIPPET.ALIASES_TITLE') }}</p>
                <p>{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.SNIPPET.ALIASES_BODY') }}</p>
                <ul class="mt-1 ml-4 list-disc">
                  <li><b>{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.SNIPPET.ALIAS_NAME') }}</b>: {{ ALL_FIELD_ALIASES.name }}</li>
                  <li><b>{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.SNIPPET.ALIAS_PHONE') }}</b>: {{ ALL_FIELD_ALIASES.phone }}</li>
                  <li><b>{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.SNIPPET.ALIAS_EMAIL') }}</b>: {{ ALL_FIELD_ALIASES.email }}</li>
                </ul>
              </div>
            </div>
          </details>
        </section>

        <section v-if="endpoint.endpoint_type === 'webhook'" class="mb-6">
          <details>
            <summary class="text-sm font-semibold text-n-slate-12 cursor-pointer">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.SNIPPET.ADVANCED_FORM') }}</summary>
            <p class="text-xs text-n-slate-11 my-2">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.SNIPPET.ADVANCED_FORM_HINT') }}</p>
            <div class="bg-n-slate-2 border border-n-slate-4 rounded p-3 relative">
              <pre class="text-xs font-mono whitespace-pre-wrap text-n-slate-12">{{ advancedFormHtml }}</pre>
              <button class="absolute top-2 right-2 text-xs px-2 py-1 rounded border border-n-slate-4 bg-n-background hover:bg-n-slate-3" @click="copyText(advancedFormHtml)">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.SNIPPET.COPY') }}</button>
            </div>
          </details>
        </section>

        <section v-if="endpoint.endpoint_type === 'webhook'" class="mb-6">
          <details>
            <summary class="text-sm font-semibold text-n-slate-12 cursor-pointer">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.SNIPPET.API') }}</summary>
            <p class="text-xs text-n-slate-11 my-2">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.SNIPPET.API_HINT') }}</p>
            <div class="bg-n-slate-2 border border-n-slate-4 rounded p-3 relative">
              <pre class="text-xs font-mono whitespace-pre-wrap text-n-slate-12">{{ curlExample }}</pre>
              <button class="absolute top-2 right-2 text-xs px-2 py-1 rounded border border-n-slate-4 bg-n-background hover:bg-n-slate-3" @click="copyText(curlExample)">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.SNIPPET.COPY_CURL') }}</button>
            </div>
          </details>
        </section>
      </div>

      <footer class="px-6 py-4 border-t border-n-slate-4 flex justify-end">
        <button class="px-4 py-2 rounded-md border border-n-slate-4 text-sm text-n-slate-12 hover:bg-n-slate-3" @click="$emit('close')">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.SNIPPET.CLOSE') }}</button>
      </footer>
    </div>
  </div>
</template>
